import { useMemo, useState } from 'react';
import { playPitches } from '../audio/player';
import { Score } from '../components/Score';
import { Button } from '../design/Button';
import { Card } from '../design/Feedback';
import { buildInterval, INTERVALS, INTERVAL_LIST, type IntervalId } from '../theory/intervals';
import { pitchName } from '../theory/pitch';
import { COMMON_TONICS } from '../theory/tonics';

interface Question {
  rootIndex: number;
  answer: IntervalId;
  choices: IntervalId[];
}

function makeQuestion(): Question {
  const rootIndex = Math.floor(Math.random() * COMMON_TONICS.length);
  const answer = INTERVAL_LIST[Math.floor(Math.random() * INTERVAL_LIST.length)].id;
  const others = INTERVAL_LIST.filter((item) => item.id !== answer)
    .sort(() => Math.random() - 0.5)
    .slice(0, 2)
    .map((item) => item.id);
  return { rootIndex, answer, choices: [answer, ...others].sort(() => Math.random() - 0.5) };
}

export function IntervalsLab() {
  const [rootIndex, setRootIndex] = useState(0);
  const [intervalId, setIntervalId] = useState<IntervalId>('M3');
  const [question, setQuestion] = useState<Question | null>(null);
  const [answer, setAnswer] = useState<string | null>(null);

  const root = COMMON_TONICS[rootIndex].pitch;
  const info = INTERVALS[intervalId];
  const notes = useMemo(() => buildInterval(root, intervalId), [root, intervalId]);

  const ask = () => {
    const next = makeQuestion();
    setQuestion(next);
    setAnswer(null);
    void playPitches(buildInterval(COMMON_TONICS[next.rootIndex].pitch, next.answer), 'arpeggio', 68);
  };

  const choose = (choice: IntervalId) => {
    if (!question) return;
    const correct = choice === question.answer;
    setAnswer(correct ? 'Yes! You got it.' : `It was ${INTERVALS[question.answer].name}. Try it again.`);
  };

  return (
    <div className="feature intervals">
      <header className="feature-head">
        <div>
          <p className="eyebrow">Intervals</p>
          <h2>Hear the space between two notes.</h2>
          <p className="lede">An interval is the space from one note to the next. See it. Count it. Hear it.</p>
        </div>
      </header>

      <Card title="Pick two notes">
        <div className="intervals__pickers">
          <label>
            <span>Start note</span>
            <select value={rootIndex} onChange={(event) => setRootIndex(Number(event.target.value))}>
              {COMMON_TONICS.map((tonic, index) => <option key={tonic.label} value={index}>{tonic.label}</option>)}
            </select>
          </label>
          <label>
            <span>Interval</span>
            <select value={intervalId} onChange={(event) => setIntervalId(event.target.value as IntervalId)}>
              {INTERVAL_LIST.map((item) => <option key={item.id} value={item.id}>{item.name} · {item.id}</option>)}
            </select>
          </label>
        </div>

        <div className="intervals__hero">
          <div>
            <p className="intervals__symbol">{info.id}</p>
            <h3>{info.name}</h3>
            <p>{pitchName(notes[0])} to {pitchName(notes[1])}</p>
          </div>
          <div className="intervals__count" aria-label={`${info.semitones} half steps`}>
            <strong>{info.semitones}</strong>
            <span>half step{info.semitones === 1 ? '' : 's'}</span>
          </div>
        </div>

        <Score clef="treble" groups={notes.map((note) => [note])} labels={['start', info.id]} />
        <div className="intervals__lesson">
          <p><strong>Count letters:</strong> {info.numberName}. Count both notes.</p>
          <p><strong>Listen:</strong> {info.copy}</p>
        </div>
        <div className="modes__actions">
          <Button variant="primary" icon="play" onClick={() => void playPitches(notes, 'arpeggio', 68)}>
            Hear one by one
          </Button>
          <Button variant="secondary" icon="play" onClick={() => void playPitches(notes, 'block')}>
            Hear together
          </Button>
        </div>
      </Card>

      <Card title="Quick ear game">
        <p className="modes__note">Hear two notes. Pick the space you hear.</p>
        <div className="modes__actions">
          <Button variant="primary" icon="play" onClick={ask}>{question ? 'New sound' : 'Start'}</Button>
          {question ? (
            <Button
              variant="tertiary"
              icon="play"
              onClick={() => void playPitches(buildInterval(COMMON_TONICS[question.rootIndex].pitch, question.answer), 'arpeggio', 68)}
            >
              Hear again
            </Button>
          ) : null}
        </div>
        {question ? (
          <div className="intervals__choices" role="group" aria-label="Interval choices">
            {question.choices.map((choice) => (
              <Button key={choice} variant="secondary" onClick={() => choose(choice)}>
                {INTERVALS[choice].name}
              </Button>
            ))}
          </div>
        ) : null}
        {answer ? <p className="intervals__answer" role="status">{answer}</p> : null}
      </Card>
    </div>
  );
}
